import { isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Dimmer, Grid, Loader } from "semantic-ui-react";
import { useGetEnergyType } from "../../../api/common";
import {
  Energy,
  HeatSource,
  RefrigerantSource,
  useEditFacility,
  WaterSource
} from "../../../api/facility";
import { useDeleteUploadProductImage } from "../../../api/product";
import {
  useGetCountries,
  useGetHeatSourceType,
  useGetRefrigerantSourceType,
  useGetWaterSourceType
} from "../../../api/static-data";
import { CommonModal } from "../../../components";
import CommonSplitContainer from "../../../components/commonSplitContainer/CommonSplitContainer";
import ConfirmModal from "../../../components/confirmViewModal/ConfirmModal";
import MainBottomMultipleButtonView from "../../../components/mainBottomButtonView/MainBottomMultipleButtonView";
import { FACILITY_OWNERSHIP, FACILITY_TYPES, UNIT_TYPES } from "../../../config/constants";
import {
  DEFAULT_ENARGY_SOURCE,
  DEFAULT_HEAT_SOURCE,
  DEFAULT_REFIGERATION_SOURCE,
  DEFAULT_WATER_SOURCE
} from "../../../config/drawingConstants";
import { errorView } from "../../../helpers/ErrorHandler";
import useWindowDimensions from "../../../hooks/windowsSize";
import { hasValidationErrors } from "../../../utils/utils";
import FacilityEnergy from "../facilityForms/FacilityEnergy";
import FacilityHeatSource from "../facilityForms/FacilityHeatSource";
import FacilityInfo from "../facilityForms/FacilityInfo";
import FacilityRefrigerant from "../facilityForms/FacilityRefrigerant";
import FacilityWaterSource from "../facilityForms/FacilityWaterSource";
import "./facilityEdit.scss";

const FacilityEditModal = ({
  visibleModal,
  setVisibleModal,
  facilityData,
  updateFacilityData
}: any) => {
  const [addFacilityEnergy, setFacilityEnergy] = useState<Energy[]>([]);
  const [visibleDeleteModal, setVisibleDeleteModal] = useState<boolean>(false);
  const [visibleHeatDeleteModal, setVisibleHeatDeleteModal] = useState<boolean>(false);
  const [visibleRefrigerantDeleteModal, setVisibleRefrigerantDeleteModal] =
    useState<boolean>(false);
  const [visibleWaterDeleteModal, setVisibleWaterDeleteModal] = useState<boolean>(false);

  const [defaultEnergyType, setDefaultEnergyType] = useState<number>(DEFAULT_ENARGY_SOURCE);
  const [deleteData, setDeleteData] = useState<Energy | null>(null);
  const [deleteHeatData, setDeleteHeatData] = useState<HeatSource | null>(null);
  const [deleteRefrigerantData, setDeleteRefrigerantData] = useState<RefrigerantSource | null>(
    null
  );
  const [deleteWaterData, setDeleteWaterData] = useState<WaterSource | null>(null);

  const [defaultWaterType, setDefaultWaterType] = useState<number>(DEFAULT_WATER_SOURCE);
  const [addFacilityWater, setFacilityWater] = useState<WaterSource[]>([]);
  const [defaultFacilityType, setDefaultType] = useState<string>(FACILITY_TYPES[0].value);
  const [defaultFacilityOwnerShip, setDefaultOwnerShip] = useState<string>(
    FACILITY_OWNERSHIP[0].value
  );
  const [getDefaultUnit, setUnit] = useState<string>(UNIT_TYPES[0].value);
  const [getDefaultRefrigerantUnit, setRefrigerantUnit] = useState<string>(UNIT_TYPES[0].value);
  const [defaultHeatType, setDefaultHeatType] = useState<number>(DEFAULT_HEAT_SOURCE);

  const [addFacilityHeat, setFacilityHeat] = useState<HeatSource[]>([]);
  const [addFacilityRefrigerant, setFacilityRefrigerant] = useState<RefrigerantSource[]>([]);

  const [country, setCountry] = useState<string>("");
  const { height } = useWindowDimensions();
  const [defaultRefrigerantType, setDefaultRefrigerantType] = useState<number>(
    DEFAULT_REFIGERATION_SOURCE
  );

  //get energy type data
  const { data: energyType, status, fetchStatus } = useGetEnergyType();

  const { isLoading: editFacilityLoading, mutate: editFacilityData } = useEditFacility();

  const { isLoading: isLoadingProductImageDelete, mutate: deleteProductImage } =
    useDeleteUploadProductImage();

  const deleteFacilityEnergy = () => {
    if (visibleDeleteModal) {
      const indexToDelete = facilityData?.energy.findIndex((item: any) => item === deleteData);
      if (indexToDelete !== -1) {
        const updatedEnergy = [
          ...facilityData.energy.slice(0, indexToDelete),
          ...facilityData.energy.slice(indexToDelete + 1)
        ];
        updateFacilityData((prev: any) => ({ ...prev, energy: updatedEnergy }));
        setDeleteData(null);
      }
    } else if (visibleHeatDeleteModal) {
      const indexToDelete = facilityData?.heatSource.findIndex(
        (item: any) => item === deleteHeatData
      );
      if (indexToDelete !== -1) {
        const updatedHeatSource = [
          ...facilityData.heatSource.slice(0, indexToDelete),
          ...facilityData.heatSource.slice(indexToDelete + 1)
        ];
        updateFacilityData((prev: any) => ({
          ...prev,
          heatSource: updatedHeatSource
        }));
        setDeleteHeatData(null);
      }
    } else if (visibleRefrigerantDeleteModal) {
      const indexToDelete = facilityData?.refrigerantSource.findIndex(
        (item: any) => item === deleteRefrigerantData
      );
      if (indexToDelete !== -1) {
        const updateRefrigerant = [
          ...facilityData.refrigerantSource.slice(0, indexToDelete),
          ...facilityData.refrigerantSource.slice(indexToDelete + 1)
        ];
        updateFacilityData((prev: any) => ({
          ...prev,
          refrigerantSource: updateRefrigerant
        }));
        setDeleteRefrigerantData(null);
      }
    } else {
      const indexToDelete = facilityData?.waterSource.findIndex(
        (item: any) => item === deleteWaterData
      );
      if (indexToDelete !== -1) {
        const updatedWaterSource = [
          ...facilityData.waterSource.slice(0, indexToDelete),
          ...facilityData.waterSource.slice(indexToDelete + 1)
        ];
        updateFacilityData((prev: any) => ({
          ...prev,
          waterSource: updatedWaterSource
        }));
        setDeleteWaterData(null);
      }
    }
  };

  const {
    data: countriesData,
    isSuccess: isCountriesDataSuccess,
    isLoading: isCountriesDataLoading
  } = useGetCountries();

  const {
    data: heatSource,
    status: heatSourceStatus,
    fetchStatus: fetchHeatSource,
    refetch: refetchHeatSource
  } = useGetHeatSourceType();
  const {
    data: waterSource,
    status: waterSourceStatus,
    fetchStatus: fetchWaterSource,
    refetch: refetchWaterSource
  } = useGetWaterSourceType();

  //handle company data input data
  const values = useMemo(() => {
    setFacilityEnergy(facilityData?.energy);
    setDefaultType(facilityData?.facilityType);
    setCountry(facilityData?.facilityCountry);
    setFacilityHeat(facilityData?.heatSource || []);
    setFacilityWater(facilityData?.waterSource || []);
    setFacilityRefrigerant(facilityData?.refrigerantSource || []);
    setDefaultOwnerShip(facilityData?.facilityOwnership || FACILITY_OWNERSHIP[0].value);
    return {
      facilityName: facilityData?.facilityName,
      facilityAddress: facilityData?.facilityAddress,
      facilityCity: facilityData?.facilityCity,
      facilityCountry: facilityData?.facilityCountry
    };
  }, [facilityData, visibleModal]);

  const countries = useMemo(() => {
    if (isCountriesDataSuccess) {
      return countriesData?.map((c: any) => ({
        key: c.id,
        text: c.country,
        value: c.id
      }));
    }
  }, [countriesData, isCountriesDataSuccess]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    getValues,
    setValue: setFacilityValue
  } = useForm({
    defaultValues: values,
    values: values
  });

  const {
    formState: { errors: errorsFacilityWater },
    handleSubmit: handleSubmitFacilityWater,
    setValue: setWaterValue,
    reset: resetWater,
    control: controlWater
  } = useForm();

  const {
    data: refrigerantSource,
    status: refrigerantStatus,
    fetchStatus: refrigerantFetchStatus,
    refetch: refrigerantFetch
  } = useGetRefrigerantSourceType();

  const {
    formState: { errors: errorRefrigerant },
    handleSubmit: handleSubmitFacilityRefrigerant,
    setValue: setRefrigerantValue,
    reset: resetRefrigerant,
    control: controlRefrigerant
  } = useForm();

  const {
    register: registerFacilityEnergy,
    formState: { errors: errorsFacilityEnergy },
    handleSubmit: handleSubmitFacilityEnergy,
    setValue: setEnergyValue,
    reset: resetUnitSize,
    setError,
    control
  } = useForm();

  const {
    register: registerHeat,
    formState: { errors: errorsFacilityHeat },
    handleSubmit: handleSubmitFacilityHeat,
    setValue: setHeatValue,
    reset: resetHeat,
    control: controlHeat
  } = useForm();

  useEffect(() => {
    resetUnitSize();
    resetHeat();
    resetWater();
  }, [visibleModal]);

  //check product error view
  const checkFacilitySaveCondition = (data: any) => {
    if (isEmpty(data.facilityName.trim())) {
      errorView("Facility name is required");
      return true;
    } else if (!country) {
      errorView("Facility country is required");
      return true;
    } else if (addFacilityEnergy.length <= 0) {
      errorView("Please add facility energy");
      return true;
    } else {
      return false;
    }
  };

  // Edit facility data
  const onSubmit = async (data: any) => {
    if (!checkFacilitySaveCondition(data)) {
      let { facilityName, facilityAddress, facilityCity, facilityCountry } = data;
      let facilityDetails = {
        _id: facilityData?._id,
        facilityName,
        facilityAddress,
        facilityCity,
        facilityCountry: country,
        facilityType: defaultFacilityType,
        energy: facilityData.energy,
        heatSource: facilityData.heatSource,
        facilityOwnership: defaultFacilityOwnerShip,
        waterSource: facilityData.waterSource,
        refrigerantSource: facilityData.refrigerantSource
      };
      editFacilityData(
        { data: facilityDetails, simulatedFacility: false },
        {
          onSuccess: () => {
            setVisibleModal(true);
            reset();
            resetUnitSize();
            setDefaultEnergyType(DEFAULT_ENARGY_SOURCE);
            resetHeat();
            resetRefrigerant();
          }
        }
      );
      if (!data?.enableNavigate || false) {
        setVisibleModal(true);
      }
    }
  };

  const saveFacilityData = () => {
    const values = getValues();
    onSubmit(values);
  };

  if (
    editFacilityLoading ||
    isLoadingProductImageDelete ||
    (status == "loading" && fetchStatus == "fetching") ||
    (heatSourceStatus == "loading" && fetchHeatSource == "fetching") ||
    (waterSourceStatus == "loading" && fetchWaterSource == "fetching") ||
    (refrigerantStatus == "loading" && refrigerantFetchStatus == "fetching")
  ) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  const checkFacilityEnergyError = hasValidationErrors(
    errors,
    errorsFacilityEnergy,
    errorsFacilityHeat,
    errorsFacilityWater,
    errorRefrigerant
  );

  return (
    <>
      <CommonModal
        modalOpenState={visibleModal}
        modalCloseState={() => {
          setVisibleModal(false);
        }}
        centered={true}
        title="Edit Facility"
      >
        <Grid>
          <Grid.Column computer={16} tablet={16} mobile={16}>
            <CommonSplitContainer
              currentHeight={0.91}
              topChild={
                <>
                  <FacilityInfo
                    register={register}
                    errors={errors}
                    country={country}
                    countries={countries}
                    setFacilityValue={setFacilityValue}
                    setCountry={setCountry}
                    isCountriesDataLoading={isCountriesDataLoading}
                    FACILITY_TYPES={FACILITY_TYPES}
                    defaultFacilityType={defaultFacilityType}
                    setDefaultType={setDefaultType}
                    defaultFacilityOwnerShip={defaultFacilityOwnerShip}
                    setDefaultOwnerShip={setDefaultOwnerShip}
                  />
                  <FacilityEnergy
                    energyType={energyType}
                    defaultEnergyType={defaultEnergyType}
                    setDefaultEnergyType={setDefaultEnergyType}
                    registerFacilityEnergy={registerFacilityEnergy}
                    errorsFacilityEnergy={errorsFacilityEnergy}
                    handleSubmitFacilityEnergy={handleSubmitFacilityEnergy}
                    addFacilityEnergy={addFacilityEnergy}
                    currentFacilityData={facilityData?.energy || []}
                    setDeleteData={setDeleteData}
                    setVisibleDeleteModal={setVisibleDeleteModal}
                    control={control}
                    setError={setError}
                    setValue={setEnergyValue}
                    reset={resetUnitSize}
                  />
                  <FacilityHeatSource
                    heatType={heatSource}
                    defaultHeatType={defaultHeatType}
                    setDefaultHeatType={setDefaultHeatType}
                    registerFacilityHeat={registerHeat}
                    errorsFacilityHeat={errorsFacilityHeat}
                    handleSubmitFacilityHeat={handleSubmitFacilityHeat}
                    addFacilityHeat={addFacilityHeat}
                    currentFacilityHeatData={addFacilityHeat || []}
                    setDeleteHeatData={setDeleteHeatData}
                    setVisibleDeleteHeatModal={setVisibleHeatDeleteModal}
                    control={controlHeat}
                    setValue={setHeatValue}
                    setUnit={setUnit}
                    getDefaultUnit={getDefaultUnit}
                    reset={resetHeat}
                  />
                  <FacilityWaterSource
                    waterType={waterSource}
                    defaultWaterType={defaultWaterType}
                    setDefaultWaterType={setDefaultWaterType}
                    errorsFacilityWater={errorsFacilityWater}
                    handleSubmitFacilityWater={handleSubmitFacilityWater}
                    addFacilityWater={addFacilityWater}
                    currentFacilityWaterData={addFacilityWater || []}
                    setDeleteWaterData={setDeleteWaterData}
                    setVisibleDeleteWaterModal={setVisibleWaterDeleteModal}
                    control={controlWater}
                    setValue={setWaterValue}
                    reset={resetWater}
                  />
                  <FacilityRefrigerant
                    refrigerantType={refrigerantSource}
                    defaultRefrigerantType={defaultRefrigerantType}
                    setDefaultRefrigerantType={setDefaultRefrigerantType}
                    errorsFacilityRefrigerant={errorRefrigerant}
                    handleSubmitFacilityRefrigerant={handleSubmitFacilityRefrigerant}
                    addFacilityRefrigerant={addFacilityRefrigerant}
                    currentFacilityRefrigerantData={addFacilityRefrigerant || []}
                    setDeleteRefrigerantData={setDeleteRefrigerantData}
                    setVisibleDeleteRefrigerantModal={setVisibleRefrigerantDeleteModal}
                    control={controlRefrigerant}
                    setValue={setRefrigerantValue}
                    setUnit={setRefrigerantUnit}
                    getDefaultUnit={getDefaultRefrigerantUnit}
                    reset={resetRefrigerant}
                  />
                </>
              }
              bottomChild={
                <MainBottomMultipleButtonView
                  leftButtons={[
                    {
                      title: "Cancel",
                      onClick: () => setVisibleModal(false),
                      buttonOutLine: true,
                      buttonView: true
                    }
                  ]}
                  rightButtons={[
                    {
                      contentView: 16,
                      title: "Save Facility",
                      onClick: () => handleSubmit(saveFacilityData)(),
                      type: "submit",
                      buttonView: true,
                      disabled: checkFacilityEnergyError
                    }
                  ]}
                />
              }
            />
          </Grid.Column>
        </Grid>
      </CommonModal>
      <ConfirmModal
        viewModal={
          visibleDeleteModal ||
          visibleHeatDeleteModal ||
          visibleWaterDeleteModal ||
          visibleRefrigerantDeleteModal
        }
        closeModal={() => setVisibleDeleteModal(false)}
        cancel={() => {
          setVisibleDeleteModal(false);
          setDeleteData(null);
          setVisibleHeatDeleteModal(false);
          setDeleteHeatData(null);
          setVisibleWaterDeleteModal(false);
          setDeleteWaterData(null);
          setVisibleRefrigerantDeleteModal(false);
          setDeleteRefrigerantData(null);
        }}
        approve={() => {
          setVisibleDeleteModal(false);
          setVisibleHeatDeleteModal(false);
          setVisibleWaterDeleteModal(false);
          setVisibleRefrigerantDeleteModal(false);
          deleteFacilityEnergy();
        }}
        title="Delete Facility Energy"
        subTitle="Are you sure you want to delete facility energy?"
      />
    </>
  );
};

export default FacilityEditModal;
